import { onMounted, onUnmounted, type ComputedRef, type Ref } from "vue";

import type { ActionConfig } from "../types";

/** ShortcutConfig from the PHP DTO */
export interface ShortcutConfig {
  key: string;
  code: string;
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  meta: boolean;
}

/** Options for useKeyboardShortcuts */
export interface UseKeyboardShortcutsOptions {
  /** Skip when focus is in input/textarea elements */
  skipInputs?: boolean;
  /** Custom selector for elements to skip */
  skipSelector?: string;
  /** Whether shortcuts are enabled */
  enabled?: Ref<boolean> | ComputedRef<boolean> | boolean;
}

/**
 * Composable for registering keyboard shortcuts from action configs.
 *
 * Handles:
 * - Global keydown listener
 * - Matching shortcuts with modifiers
 * - Skipping when in input/textarea elements
 */
export function useKeyboardShortcuts(
  actions: Ref<ActionConfig[]> | ComputedRef<ActionConfig[]>,
  onTrigger: (action: ActionConfig) => void,
  options: UseKeyboardShortcutsOptions = {}
) {
  const {
    skipInputs = true,
    skipSelector = 'input, textarea, [contenteditable="true"]'
  } = options;

  function shouldSkip(event: KeyboardEvent): boolean {
    if (!skipInputs) return false;

    const target = event.target as Element | null;
    if (!target) return false;

    // Check if target matches skip selector
    if (target.matches?.(skipSelector)) return true;

    // Also check if we're inside an element that matches
    if (target.closest?.(skipSelector)) return true;

    return false;
  }

  function matchesShortcut(
    event: KeyboardEvent,
    shortcut: ShortcutConfig
  ): boolean {
    // Check key match (case-insensitive for letters)
    const eventKey = event.key.toLowerCase();
    const shortcutCode = shortcut.code.toLowerCase();

    if (eventKey !== shortcutCode) return false;

    // Check modifiers
    const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

    // Ctrl on Windows/Linux = Cmd on Mac when ctrl is specified
    const ctrlMatch = shortcut.ctrl
      ? isMac
        ? event.metaKey
        : event.ctrlKey
      : !event.ctrlKey && !event.metaKey;

    if (!ctrlMatch && shortcut.ctrl) return false;

    // Only check exact match if no ctrl modifier
    if (!shortcut.ctrl && (event.ctrlKey || event.metaKey)) return false;

    if (shortcut.shift !== event.shiftKey) return false;
    if (shortcut.alt !== event.altKey) return false;

    // Meta (Cmd/Win key) - only if explicitly required
    if (shortcut.meta && !event.metaKey) return false;

    return true;
  }

  function handleKeydown(event: KeyboardEvent): void {
    // Check if enabled
    const enabled = options.enabled;
    if (enabled !== undefined) {
      const isEnabled = typeof enabled === "boolean" ? enabled : enabled.value;
      if (!isEnabled) return;
    }

    // Skip if in input elements
    if (shouldSkip(event)) return;

    // Find matching action
    for (const action of actions.value) {
      if (!action.shortcut) continue;

      // Access shortcut from the action - it may be the DTO type
      const shortcut = action.shortcut as ShortcutConfig;
      if (matchesShortcut(event, shortcut)) {
        event.preventDefault();
        event.stopPropagation();
        onTrigger(action);
        return;
      }
    }
  }

  onMounted(() => {
    window.addEventListener("keydown", handleKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener("keydown", handleKeydown);
  });

  return {
    matchesShortcut
  };
}

/**
 * Format a shortcut config for display.
 * Platform-aware: "⌘D" on Mac, "Ctrl+D" on Windows/Linux.
 */
export function formatShortcut(
  shortcut: ShortcutConfig | null | undefined
): string | null {
  if (!shortcut) return null;

  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
  const parts: string[] = [];

  if (shortcut.ctrl) {
    parts.push(isMac ? "⌘" : "Ctrl");
  }
  if (shortcut.meta) {
    parts.push(isMac ? "⌘" : "Win");
  }
  if (shortcut.alt) {
    parts.push(isMac ? "⌥" : "Alt");
  }
  if (shortcut.shift) {
    parts.push(isMac ? "⇧" : "Shift");
  }

  parts.push(shortcut.key);

  return isMac ? parts.join("") : parts.join("+");
}

/**
 * Get shortcut display from an action config.
 */
export function getActionShortcutDisplay(
  action: ActionConfig | undefined
): string | null {
  if (!action?.shortcut) return null;
  return formatShortcut(action.shortcut as ShortcutConfig);
}
